import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Trans, useTranslation } from "react-i18next";
import { IdCard, KeyRound } from "lucide-react";

import authClient from "../single-page/auth/authClient.js";
import LoadingButton from "../single-page/components/LoadingButton.jsx";
import { formatDocument } from "../single-page/lib/documentMask.js";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import logoParvi from "../images/parvi/logo-parvi.png";
import logoParviWhite from "../images/parvi/logo-parvi-white.png";
import dealershipPhoto from "../images/parvi/dealership.jpg";
import dealershipPhoto2 from "../images/parvi/dealership-2.jpg";

// Tela de acesso do Customer — tela inicial do sistema (raiz /), em duas
// etapas na mesma rota:
//
//   1. CPF ou CNPJ — POST /api/customers/request_code (envia código por e-mail)
//   2. Código      — POST /api/customers/sign_in (cria a sessão)
//
// Reenvio respeita o rate limit do backend (60s); o contador local é só UX —
// a fonte da verdade é o 429 com retry_in. O 429 com `throttled: true` é outro
// caso (Rack::Attack, muitas tentativas por IP): vira mensagem na etapa atual,
// sem avançar — ver .docs/0k - 20-plano-login-cpf-cnpj.md (§8).
//
// Layout do Figma "Parvi — Sistema" (frames 1-35 desktop / 42-2133 mobile):
// desktop é split-screen (coluna do form à esquerda, foto de concessionária
// com véu bg-primary/30 à direita); no mobile a foto cobre a tela inteira e o
// card flutua por cima — por isso o logo/textos têm versão branca fora do lg.
// Cores sempre via tokens do tema (ver theme_tokens_test).

const RESEND_SECONDS = 60;

// Fotos de fundo: uma é sorteada a cada acesso à tela (frame Figma 42-1070
// trouxe a segunda fachada). O sorteio fica num initializer de useState para
// não trocar a foto em re-renders da mesma visita.
const BACKGROUND_PHOTOS = [dealershipPhoto, dealershipPhoto2];

// Retry-After do throttle (Rack::Attack), em segundos — rearma o contador de
// reenvio para o botão não ficar clicável em loop. Sem header válido, cai no
// intervalo padrão.
function retryAfterSeconds(response) {
  const seconds = Number(response?.headers?.["retry-after"]);
  return Number.isFinite(seconds) && seconds > 0 ? seconds : RESEND_SECONDS;
}

export default function CustomerAccess() {
  const { t } = useTranslation();
  const [step, setStep] = useState("document"); // "document" | "code"
  // `documentNumber`, não `document`: requestCode/signIn usam document.activeElement.
  const [documentNumber, setDocumentNumber] = useState("");
  const [code, setCode] = useState("");
  const [documentError, setDocumentError] = useState();
  const [codeError, setCodeError] = useState();
  const [loading, setLoading] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [backgroundPhoto] = useState(
    () => BACKGROUND_PHOTOS[Math.floor(Math.random() * BACKGROUND_PHOTOS.length)],
  );
  const timerRef = useRef();

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    timerRef.current = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(timerRef.current);
  }, [resendIn]);

  function requestCode(event) {
    event?.preventDefault();
    document.activeElement.blur();
    toast.dismiss();
    setDocumentError();
    setLoading(true);

    authClient
      .requestAccessCode(documentNumber)
      .then(() => {
        toast.success(t("auth.access.codeSent"));
        setStep("code");
        setCode("");
        setCodeError();
        setResendIn(RESEND_SECONDS);
      })
      .catch((error) => {
        const status = error.response?.status;
        const data = error.response?.data;
        if (status === 422) {
          setDocumentError(data?.error ?? t("auth.access.invalidDocument"));
        } else if (status === 429 && data?.throttled) {
          // Throttle por IP (Rack::Attack) — não avança de etapa. O "Reenviar
          // código" também cai aqui: na etapa do código o erro vai para baixo
          // do campo do código e o contador é rearmado com o Retry-After.
          const message = data.error ?? t("auth.access.tooManyAttempts");
          if (step === "document") {
            setDocumentError(message);
          } else {
            setCodeError(message);
            setResendIn(retryAfterSeconds(error.response));
          }
        } else if (status === 429) {
          // Já existe código válido recente — segue para a etapa do código.
          setStep("code");
          setResendIn(data?.retry_in ?? RESEND_SECONDS);
        } else {
          toast.error(t("toasts.genericError"));
        }
      })
      .finally(() => setLoading(false));
  }

  function signIn(event) {
    event.preventDefault();
    document.activeElement.blur();
    toast.dismiss();
    setCodeError();
    setLoading(true);

    authClient
      .signInCustomer(documentNumber, code)
      .then(() => {
        // Sem mensagem na URL: Customers::SessionsController grava o flash
        // mesmo em JSON, e ele chega ao SPA pela sessão.
        window.location.href = "/site";
      })
      .catch((error) => {
        const status = error.response?.status;
        const data = error.response?.data;
        if (status === 429 && data?.throttled) {
          setCodeError(data.error ?? t("auth.access.tooManyAttempts"));
        } else if (status === 401) {
          setCodeError(data?.error ?? t("auth.access.invalidCode"));
        } else {
          toast.error(t("toasts.genericError"));
        }
      })
      .finally(() => setLoading(false));
  }

  return (
    <div className="relative min-h-screen lg:grid lg:grid-cols-2">
      {/* Foto: fundo da tela inteira no mobile, coluna direita no desktop.
          O véu translúcido usa o primary do tema para acompanhar o rebrand. */}
      <div className="absolute inset-0 lg:relative lg:order-2">
        <img src={backgroundPhoto} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-primary/30" aria-hidden="true" />
      </div>

      <div className="relative flex min-h-screen flex-col items-center justify-center gap-6 px-4 py-10 sm:px-8 lg:order-1 lg:min-h-0 lg:bg-muted">
        {/* Logo: branco sobre a foto (mobile) e sobre fundo escuro (desktop dark);
            colorido só no desktop light. */}
        <img src={logoParviWhite} alt="Grupo Parvi" className="h-14 w-auto lg:hidden" />
        <img src={logoParvi} alt="Grupo Parvi" className="hidden h-16 w-auto lg:block dark:lg:hidden" />
        <img src={logoParviWhite} alt="Grupo Parvi" className="hidden h-16 w-auto dark:lg:block" />

        <p className="max-w-md text-center text-lg font-light text-white lg:text-muted-foreground">
          <Trans i18nKey="auth.access.tagline" components={{ bold: <span className="font-medium" /> }} />
        </p>

        <Card className="w-full max-w-lg rounded-2xl shadow-md">
          <CardContent className="space-y-6 p-6 pt-8 sm:p-8">
            <div className="space-y-1 text-center">
              <p className="text-lg font-light">{t("auth.access.welcome")}</p>
              <p className="text-xl font-bold text-primary sm:text-2xl">{t("auth.access.portalName")}</p>
            </div>

            <p className="text-center text-sm font-light text-muted-foreground sm:text-base">
              {step === "document" ? (
                <Trans i18nKey="auth.access.documentHint" components={{ bold: <span className="font-semibold" /> }} />
              ) : (
                t("auth.access.codeHint")
              )}
            </p>

            {step === "document" ? (
              <form className="space-y-5" onSubmit={requestCode} noValidate>
                <div className="space-y-2">
                  <Label htmlFor="document" className="font-bold">{t("auth.access.documentLabel")}</Label>
                  <div className="relative">
                    <span className="pointer-events-none absolute left-2.5 top-1/2 flex h-9 w-11 -translate-y-1/2 items-center justify-center rounded bg-muted">
                      <IdCard className="h-6 w-6 text-primary" />
                    </span>
                    {/* inputMode numérico mantido: o CNPJ alfanumérico é edge aceito (plano 20, §10). */}
                    <Input
                      id="document"
                      inputMode="numeric"
                      autoComplete="off"
                      autoFocus
                      placeholder={t("auth.access.documentPlaceholder")}
                      value={documentNumber}
                      onChange={(e) => setDocumentNumber(formatDocument(e.target.value))}
                      className={`h-14 pl-16 text-base ${documentError ? "border-destructive focus-visible:ring-destructive" : ""}`}
                    />
                  </div>
                  {documentError && <p className="text-sm text-destructive">{documentError}</p>}
                </div>

                <LoadingButton type="submit" loading={loading} fullWidth className="h-12 text-base">
                  {t("auth.access.sendCode")}
                </LoadingButton>
              </form>
            ) : (
              <form className="space-y-5" onSubmit={signIn} noValidate>
                <div className="space-y-2">
                  <Label htmlFor="access-code" className="font-bold">{t("auth.access.code")}</Label>
                  <div className="relative">
                    <span className="pointer-events-none absolute left-2.5 top-1/2 flex h-9 w-11 -translate-y-1/2 items-center justify-center rounded bg-muted">
                      <KeyRound className="h-6 w-6 text-primary" />
                    </span>
                    <Input
                      id="access-code"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      autoFocus
                      maxLength={6}
                      placeholder="000000"
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      className={`h-14 pl-16 text-center text-lg tracking-[0.5em] ${codeError ? "border-destructive focus-visible:ring-destructive" : ""}`}
                    />
                  </div>
                  {codeError && <p className="text-sm text-destructive">{codeError}</p>}
                </div>

                <LoadingButton type="submit" loading={loading} fullWidth className="h-12 text-base" disabled={code.length !== 6}>
                  {t("auth.access.submit")}
                </LoadingButton>

                <div className="flex items-center justify-between text-sm">
                  <Button type="button" variant="link" className="h-auto p-0" onClick={() => setStep("document")}>
                    {t("auth.access.changeDocument")}
                  </Button>

                  {resendIn > 0 ? (
                    <span className="text-muted-foreground">
                      {t("auth.access.resendIn", { seconds: resendIn })}
                    </span>
                  ) : (
                    <Button
                      type="button"
                      variant="link"
                      className="h-auto p-0"
                      disabled={loading}
                      onClick={() => requestCode()}
                    >
                      {t("auth.access.resend")}
                    </Button>
                  )}
                </div>
              </form>
            )}
          </CardContent>
        </Card>

        <p className="max-w-lg text-center text-xs font-light text-white sm:text-sm lg:text-muted-foreground">
          <Trans
            i18nKey="auth.access.lgpd"
            components={{ privacy: <a href="#" className="font-semibold underline lg:text-primary" /> }}
          />
        </p>
      </div>
    </div>
  );
}
